/* eslint-disable no-param-reassign */
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Member } from 'src/modules/member/models/member';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { sendGroupMessage } from 'src/shared/services/groupMessageService';
import { useTranslation } from 'react-i18next';
import { Send } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { LoadingButton } from '@mui/lab';
import { getSenderTitle } from 'src/shared/functions/getSenderTitle';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { ComposeEmailConfirmDialog } from './composeEmailConfirmDialog';
import { ComposeEmailForm } from './composeEmailForm';
import { ComposeEmail } from '../models/composeEmail';
import { SelectionTypeEnum } from '../models/selectionType.enum';

type MailModalProps = {
  modalOpen: boolean;
  members: Member[] | [];
  // onSave: () => void;
  onClose: () => void;
};

export const ComposeEmailModal = (props: MailModalProps) => {
  const { modalOpen, members, onClose } = props;
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['groupMessage', 'common', 'member']);

  const isMounted = useIsMounted();

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const requiredMessage = t('common:validationMessages.required');

  const ComposeEmailSchema: Schema<ComposeEmail> = yup.object().shape({
    recipients: yup.string().trim().optional(),
    selection_id: yup.number().required(requiredMessage),
    subject: yup
      .string()
      .required(requiredMessage)
      .max(70, t('common:validationMessages.maxLength', { count: 70 })),
    body: yup.string().required(requiredMessage),
    sender: yup.string().email(t('common:validationMessages.email')).required(requiredMessage),
    send: yup.boolean().required(),
    circularName: yup.string().optional().nullable(),
    isHTML: yup.boolean().required(),
    senderTitle: yup.string().optional(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
    includeRecipientsThatHasUnsubscribedFromEmail: yup.boolean().optional(),
  });

  const formMethods = useForm<ComposeEmail>({
    resolver: yupResolver(ComposeEmailSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      sender: currentUser?.email || '',
      body: '',
      send: true,
      circularName: '',
      senderTitle: getSenderTitle(currentUser),
      selectionType: SelectionTypeEnum.Selection,
      includeRecipientsThatHasUnsubscribedFromEmail: false,
      selection_id: 0,
      isHTML: true,
      speedFormText: '',
      speedFormValues: '',
      recipients: members
        .filter((x) => x.email && x.newsletterEmailUnsubscribed !== true)
        .map((x) => x.email)
        .join(';'),
    },
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting },
  } = formMethods;

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  // Avanmälda ska aldrig få utskicket – oavsett hur många som markerats. Tidigare spärrades bara fallet med EN
  // markerad mottagare, så vid flera gick mailet i väg även till dem som tackat nej.
  const sendableMembers = React.useMemo(() => members.filter((x) => x.newsletterEmailUnsubscribed !== true), [members]);
  const unsubscribedCount = members.length - sendableMembers.length;
  // Finns ingen kvar att skicka till är hela utskicket blockerat (samma läge som förut när den enda var avanmäld).
  const isUnsubscribed = sendableMembers.length === 0 && members.length > 0;
  const recipientsCount = sendableMembers.filter((x) => x.email).length;

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = async (data: ComposeEmail) => {
    ConsoleHelper.log('onSubmit', data);
    // await trigger();
    try {
      data.send = true;
      data.circularName = data.subject;
      data.recipients = sendableMembers
        .filter((x) => x.email)
        .map((x) => x.email)
        .join(';');
      if (!data.senderTitle) {
        data.senderTitle = data.subject;
      }
      const res = await sendGroupMessage(data);
      if (isMounted()) {
        ConsoleHelper.log('res sendGroupMessage', res);
        if (members.length === 1) {
          toast.success(<>{t('member:memberDetails.emailSendSuccess')}</>);
        } else {
          toast.success(<>{t('groupMessage:create.sendSuccess')}</>);
        }
        onHandleClose();
      }
    } catch (e) {
      if (isMounted()) {
        if (members.length === 1) {
          toast.warning(<>{t('member:memberDetails.emailSendFailed')}</>);
        } else {
          toast.warning(<>{t('groupMessage:create.sendError')}</>);
        }
        ConsoleHelper.error('saveFormDataError:', e);
      }
    }
  };

  // "Skicka"-knappen validerar formuläret och öppnar bekräftelsemodalen
  // istället för att skicka direkt. Skickande sker först i confirm-dialogen.
  const onClickSend = React.useCallback(async () => {
    const valid = await trigger();
    if (!valid) return;
    setConfirmOpen(true);
  }, [trigger]);

  const onConfirmSend = React.useCallback(() => {
    handleSubmit(async (data) => {
      await onSubmit(data);
      if (isMounted()) setConfirmOpen(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit]);

  const onHandleSave = React.useCallback(() => {
    const membersWithMail = members.filter((x) => x.email);
    if (membersWithMail.length > 0) {
      handleSubmit(onSubmit)();
      // formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      toast.error(<>{t('common:statusMessages.generalError')}</>);
    }
  }, [members]);

  const setDialogHeader = () => {
    const membersWithMail = members.filter((x) => x.email);
    if (membersWithMail.length > 0) {
      return membersWithMail.length > 1
        ? t('groupMessage:create.SendToMembers', { count: membersWithMail.length })
        : t('groupMessage:create.SendToMember', { email: membersWithMail[0]?.email });
    }
    return t('groupMessage:selection.noUserHasEmail');
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Dialog
          onClose={(_, reason) => {
            // Spärra "click outside" och Esc — användaren måste aktivt kryssa eller avbryta.
            // Förhindrar att man tappar utkast genom misstagsklick på backdroppen.
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            onHandleClose();
          }}
          aria-labelledby={`${componentId}-titel`}
          open={modalOpen}
          maxWidth='md'
          fullWidth
          disableEnforceFocus
          sx={{
            minWidth: {
              xs: '90vw',
              md: '400px',
            },
            '& .MuiDialogContent-root': {
              p: 2,
            },
            '& .MuiDialogActions-root': {
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ m: 0, p: 2, fontWeight: 600 }} id={`${componentId}-titel`}>
            {setDialogHeader()}
            <DialogCloseButton handleClose={onHandleClose} />
          </DialogTitle>
          <DialogContent dividers>
            {isUnsubscribed && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {t('member:communication.unsubscribedEmailAlert')}
              </Alert>
            )}

            {!isUnsubscribed && unsubscribedCount > 0 && (
              <Alert severity='info' sx={{ mb: 2 }}>
                {t('member:communication.unsubscribedExcludedAlert', { count: unsubscribedCount })}
              </Alert>
            )}
            <ComposeEmailForm members={sendableMembers} disabled={isUnsubscribed} />
          </DialogContent>
          <DialogActions>
            <Box sx={{ p: 1 }}>
              <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 2 }}>
                {t('common:button.cancel')}
              </Button>
              <LoadingButton
                autoFocus
                loading={isSubmitting}
                variant='contained'
                type='button'
                disabled={recipientsCount === 0 || isUnsubscribed}
                onClick={onClickSend}
                endIcon={<Send aria-hidden fontSize='small' />}
              >
                {t('common:button.send')}
              </LoadingButton>
            </Box>
          </DialogActions>
        </Dialog>
        <ComposeEmailConfirmDialog
          open={confirmOpen}
          loading={isSubmitting}
          recipientsCount={recipientsCount}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onConfirmSend}
        />
      </form>
    </FormProvider>
  );
};
